import { inject, Service } from '@angular/core'
import { EntriesService, ExternalFormsService } from '@superlikers/microsites-api'
import { firstValueFrom, forkJoin, map } from 'rxjs'

import { Notification, NotificationKind } from '../../models/notification.model'

const CATEGORY = 'notifications'
/** Acción de la que cuelgan las notificaciones personales, una entry por participante y categoría. */
const PERSONAL_ATYPE = 'notificacion_personal'
/** Acción con `use_as_form`: una entry por notificación leída, con su id como categoría. */
const READ_ATYPE = 'notificaciones_leidas'

const KINDS: NotificationKind[] = ['ticket', 'prize', 'challenge', 'news', 'alert']

/** Contenido de una notificación, venga de los fields del form o de la entry. */
interface NotificationData {
  kind?: unknown
  title?: unknown
  description?: unknown
  link?: unknown
  modal_body?: unknown
  image?: unknown
}

/**
 * HTTP puro de las notificaciones y única pieza que sabe de dónde salen:
 * - las masivas son external forms de la categoría `notifications`, con el
 *   contenido en los `default_value` de sus fields y el alcance en sus reglas;
 * - las personales son entries del participante en `notificacion_personal`;
 * - lo leído vive aparte: una entry en `notificaciones_leidas` por notificación,
 *   con el id de la notificación como `category`.
 *
 * La fecha de todas es la de creación, así el panel queda en ese orden.
 *
 * Devuelve interfaces del proyecto. Sin lógica de negocio; lanza, no maneja errores.
 */
@Service()
export class NotificationsApi {
  readonly #forms = inject(ExternalFormsService)
  readonly #entries = inject(EntriesService)

  async getNotifications(): Promise<Notification[]> {
    const [broadcast, personal, readIds] = await Promise.all([this.#getBroadcast(), this.#getPersonal(), this.getReadIds()])

    return [...broadcast, ...personal].map(item => ({ ...item, read: readIds.includes(item.id) })).sort((a, b) => b.date.getTime() - a.date.getTime())
  }

  /** Ids leídos: la `category` de cada entry de `notificaciones_leidas`. */
  async getReadIds(): Promise<string[]> {
    const read$ = this.#entries.listExternals({ atype: READ_ATYPE, limit: 500 }).pipe(map(response => response.data))
    const read = await firstValueFrom(read$)

    return read.map(entry => entry.category ?? '').filter(Boolean)
  }

  /**
   * Una entry por notificación leída. Al ser `use_as_form`, repetir la misma
   * categoría actualiza esa entry en vez de crear otra.
   */
  async markAsRead(id: string): Promise<void> {
    await firstValueFrom(this.#entries.createExternal(READ_ATYPE, { read_at: new Date().toISOString() }, { category: id }))
  }

  /** El backend ya filtra por las reglas de participante de cada form. */
  async #getBroadcast(): Promise<Notification[]> {
    const list$ = this.#forms.listForms({ page: 1, limit: 100, category: CATEGORY }).pipe(map(response => response.data))
    const list = await firstValueFrom(list$)

    // La acción de las personales comparte categoría, pero su contenido no está en los fields.
    const forms = list.filter(form => form.slug !== PERSONAL_ATYPE)
    if (forms.length === 0) return []

    const fields$ = forms.map(form => this.#forms.getFormFields(form._id).pipe(map(response => response.object)))
    const fieldsWrapper = await firstValueFrom(forkJoin(fields$))

    return fieldsWrapper.map((item, index) => {
      const data = Object.fromEntries(item.fields.map(field => [field.name, field.default_value])) as NotificationData

      return this.#adapter(item.external_action, data, forms[index].created_at)
    })
  }

  async #getPersonal(): Promise<Notification[]> {
    const personal$ = this.#entries.listExternals<NotificationData>({ atype: PERSONAL_ATYPE, limit: 100 }).pipe(map(response => response.data))
    const personal = await firstValueFrom(personal$)

    const seen = new Set<string>()

    return [...personal]
      .sort((a, b) => b.created_at - a.created_at)
      .flatMap(entry => {
        // La categoría la pone el CSV y sobrevive a las reimportaciones; el `_id` es el respaldo.
        const id = String(entry.category ?? '').trim() || entry._id
        // Con varias entries de la misma categoría (importaciones repetidas) vale la última.
        if (seen.has(id)) return []
        seen.add(id)

        return [this.#adapter(id, entry.data ?? {}, entry.created_at)]
      })
  }

  /** `createdAt` llega en segundos. */
  #adapter(id: string, data: NotificationData, createdAt: number): Notification {
    const link = this.#text(data.link)
    const modalBody = this.#text(data.modal_body)
    const image = this.#text(data.image)

    return {
      id,
      kind: this.#toKind(data.kind),
      title: this.#text(data.title),
      description: this.#text(data.description),
      date: new Date(createdAt * 1000),
      ...(link && { link }),
      ...(modalBody && { modalBody }),
      ...(image && { image }),
      read: false
    }
  }

  #text(value: unknown): string {
    return String(value ?? '').trim()
  }

  /** Un tipo desconocido no debe romper el icono: cae a `news`. */
  #toKind(value: unknown): NotificationKind {
    return KINDS.find(item => item === this.#text(value).toLowerCase()) ?? 'news'
  }
}
